// External libraries

// Internal imports
import React, { useState, useEffect } from 'react';
import { useNetworkAware } from '@/hooks/use-network-aware';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {

  Wifi,
  WifiOff,
  RefreshCw,
  Download,
  Upload,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface OfflineIndicatorProps {
  className?: string;
  showDetails?: boolean;
  compact?: boolean;
}

export function OfflineIndicator({
  className,
  showDetails = false,
  compact = false
}: OfflineIndicatorProps) {
  const {
    networkStatus,
    isSlowConnection,
    connectionQuality,
    checkConnection,
    wasOffline,
    offlineDuration
  } = useNetworkAware();

  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const handleRefresh = async () => {
    setIsChecking(true);
    try {
      await checkConnection();
      setLastChecked(new Date());
    } finally {
      setIsChecking(false);
    }
  };

  // Auto-check every 30 seconds when offline
  useEffect(() => {
    if (!networkStatus.online) {
      const interval = setInterval(handleRefresh, 30000);
      return () => clearInterval(interval);
    }
  }, [networkStatus.online]);

  const getConnectionIcon = () => {
    if (!networkStatus.online) {
      return <WifiOff className="h-4 w-4" />;
    }

    switch (connectionQuality) {
      case 'excellent':
        return <Wifi className="h-4 w-4 text-green-500" />;
      case 'good':
        return <Wifi className="h-4 w-4 text-blue-500" />;
      case 'fair':
        return <Wifi className="h-4 w-4 text-yellow-500" />;
      case 'poor':
        return <Wifi className="h-4 w-4 text-orange-500" />;
      default:
        return <Wifi className="h-4 w-4 text-gray-500" />;
    }
  };

  const getConnectionText = () => {
    if (!networkStatus.online) {
      return 'Offline';
    }

    if (isSlowConnection) {
      return 'Slow Connection';
    }

    return networkStatus.effectiveType.toUpperCase();
  };

  const getConnectionColor = () => {
    if (!networkStatus.online) {
      return 'destructive';
    }

    if (isSlowConnection) {
      return 'secondary';
    }

    return connectionQuality === 'excellent' || connectionQuality === 'good' ? 'default' : 'secondary';
  };

  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {getConnectionIcon()}
        <Badge variant={getConnectionColor()} className="text-xs">
          {getConnectionText()}
        </Badge>
      </div>
    );
  }

  return (
    <Card className={cn("w-full max-w-md", className)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {getConnectionIcon()}
            <div>
              <h3 className="font-semibold text-sm">Connection Status</h3>
              <Badge variant={getConnectionColor()} className="text-xs mt-1">
                {getConnectionText()}
              </Badge>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isChecking}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={cn("h-4 w-4", isChecking && "animate-spin")} />
          </Button>
        </div>

        {showDetails && (
          <div className="space-y-3 text-xs">
            {networkStatus.online ? (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Quality:</span>
                  <Badge variant="outline" className="capitalize">
                    {connectionQuality}
                  </Badge>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Speed:</span>
                  <span>{networkStatus.downlink} Mbps</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Latency:</span>
                  <span>{networkStatus.rtt}ms</span>
                </div>

                {networkStatus.saveData && (
                  <div className="flex items-center gap-2 text-orange-600">
                    <AlertTriangle className="h-3 w-3" />
                    <span>Data saver mode enabled</span>
                  </div>
                )}

                {wasOffline && offlineDuration > 0 && (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-3 w-3" />
                    <span>Back online after {formatDuration(offlineDuration)}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-red-600">
                  <WifiOff className="h-4 w-4" />
                  <span>No internet connection</span>
                </div>

                <div className="flex items-center gap-2 text-gray-600">
                  <Download className="h-3 w-3" />
                  <span>Offline mode enabled</span>
                </div>

                <p className="text-gray-500">
                  Some features may be unavailable. You can still access cached data.
                </p>

                {lastChecked && (
                  <div className="text-gray-400">
                    Last checked: {lastChecked.toLocaleTimeString()}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Offline actions */}
        {!networkStatus.online && (
          <div className="mt-4 space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isChecking}
              className="w-full"
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", isChecking && "animate-spin")} />
              {isChecking ? 'Checking...' : 'Retry Connection'}
            </Button>

            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                View Offline Data
              </Button>
              <Button variant="ghost" size="sm" className="flex-1">
                <Upload className="h-4 w-4 mr-2" />
                Sync Queue
              </Button>
            </div>
          </div>
        )}

        {/* Slow connection warning */}
        {networkStatus.online && isSlowConnection && (
          <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200 text-xs">
              <AlertTriangle className="h-3 w-3" />
              <span>Slow connection detected. Some features may be reduced.</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}